import type { AgentPlan, PlanNode, PlanStage } from './plan';

/**
 * Flattened view of a plan's nodes, in stage order. For each node
 * yields its global stage index (0-based), the containing stage's id,
 * and the list of node ids in the preceding stage (used to compute
 * the default-context fallback when `contextFrom` is null).
 *
 * Consumers (workflow, client, validators) should prefer this over
 * walking `plan.stages` manually so the iteration order is uniform.
 *
 * Lives in its own leaf file (no barrel deps) so the Temporal worker
 * webpack bundle can import it directly via
 * `@aline/common/agent-harness/flatten-plan-nodes` without pulling in
 * the `@aline/common` index barrel (which loads runtime zod / nanoid /
 * etc. that the workflow sandbox can't resolve under strict ESM).
 */
export interface FlattenedPlanNode<TNode = PlanNode> {
  node: TNode;
  prevStageNodeIds: number[];
  stageId: number;
  stageIndex: number;
  stageType: PlanStage['type'];
}

export function flattenPlanNodes(plan: AgentPlan): FlattenedPlanNode[] {
  return flattenStages<PlanNode>(plan.stages);
}

/**
 * Resolve the effective upstream node-id list for a plan node:
 *   - explicit `contextFrom`        → exactly those node ids
 *   - `contextFrom: null`           → the previous stage's node ids
 *   - first-stage node with `null`  → empty array
 *
 * Shared by the Temporal workflow (which dispatches priors as
 * `executeNodeActivity.priorNodeIds`) and the client (which uses the
 * same policy to infer a just-ready node before its first persisted
 * `nodeStates` update). Without a shared helper the two sides drifted
 * on the empty-array vs default-priors edge case.
 */
export function resolvePriorNodeIds(
  node: PlanNode,
  flattenedFromSamePlan: readonly FlattenedPlanNode[]
): number[] {
  if (node.contextFrom != null) return node.contextFrom.map(ref => ref.nodeId);
  const entry = flattenedFromSamePlan.find(f => f.node.id === node.id);
  return entry?.prevStageNodeIds ?? [];
}

export function flattenStages<TNode extends { id: number }>(
  stages: readonly (
    | { id: number; node: TNode; type: 'single' }
    | { id: number; nodes: TNode[]; type: 'parallel' }
  )[]
): FlattenedPlanNode<TNode>[] {
  const out: FlattenedPlanNode<TNode>[] = [];
  let prevStageNodeIds: number[] = [];
  stages.forEach((stage, stageIndex) => {
    const stageNodes = stage.type === 'single' ? [stage.node] : stage.nodes;
    stageNodes.forEach(node => {
      out.push({
        node,
        prevStageNodeIds,
        stageId: stage.id,
        stageIndex,
        stageType: stage.type,
      });
    });
    prevStageNodeIds = stageNodes.map(n => n.id);
  });
  return out;
}

/**
 * Every executing specialist except the summarizer. The summarizer only
 * condenses completed output and never receives tools. Work nodes may
 * receive a shared connected-account session when connectors are available.
 *
 * Keep this in the flatten leaf so Temporal can import it without the
 * plan schema's runtime zod graph.
 */
export function getIsWorkPlanNode(node: Pick<PlanNode, 'agent'>): boolean {
  return node.agent !== 'summarizer';
}
